import {
  Calendar,
  CreditCard,
  MessageSquare,
  Trophy,
  Smartphone,
  BarChart3,
  Zap,
  Shield,
  Users,
  Sparkles,
} from 'lucide-react';

const features = [
  {
    icon: Calendar,
    title: 'Agenda Inteligente',
    description: 'Gerencie aulas, aluguel de quadra e horários com drag-and-drop visual e detecção automática de conflitos.',
  },
  {
    icon: CreditCard,
    title: 'Cobranças Automáticas',
    description: 'PIX, cartão de crédito e boleto. Faturas automáticas, rastreio de inadimplência e lembretes inteligentes.',
  },
  {
    icon: MessageSquare,
    title: 'WhatsApp Automatizado',
    description: 'Confirmações de aula, lembretes de pagamento e comunicação com alunos direto pelo WhatsApp.',
  },
  {
    icon: Trophy,
    title: 'Torneios Completos',
    description: 'Crie campeonatos com chaves ao vivo, rankings, inscrições online e páginas públicas.',
  },
  {
    icon: Smartphone,
    title: 'App para Alunos',
    description: 'Seus alunos agendam, cancelam, veem créditos e recebem notificações direto no celular.',
  },
  {
    icon: BarChart3,
    title: 'Relatórios & Analytics',
    description: 'Receita, ocupação, inadimplência e previsões. Tudo num dashboard em tempo real.',
  },
  {
    icon: Users,
    title: 'Aula Experimental',
    description: 'Link público para aulas experimentais com conversão automática para aluno regular.',
  },
  {
    icon: Sparkles,
    title: 'IA Integrada',
    description: 'Previsão de inadimplência, sugestão de remanejamento e assistente inteligente para gestão.',
  },
];

export default function ArenaiPromoSection() {
  return (
    <section className="relative py-16 md:py-24 overflow-hidden" style={{ backgroundColor: '#0d0d0d' }}>
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '32px 32px',
          }}
        />
      </div>

      <div className="relative z-10 container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full border border-yellow-500/20 bg-yellow-500/5">
            <Zap className="w-4 h-4 text-yellow-400" />
            <span className="text-yellow-400 text-sm font-semibold uppercase tracking-wider">
              Parceria Exclusiva
            </span>
          </div>

          <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight mb-4">
            Conheça o{' '}
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: 'linear-gradient(135deg, #FFD04F, #F58A25, #F2632F)' }}
            >
              ArenAi
            </span>
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            O sistema completo de gestão para quadras de beach tennis, padel, futevôlei e society.
            Quem contrata a Apertai pode fechar os dois com condições especiais.
          </p>
        </div>

        {/* Visual showcase */}
        <div className="relative rounded-3xl p-1 mb-16 max-w-4xl mx-auto">
          <div
            className="absolute inset-0 rounded-3xl opacity-20 blur-sm"
            style={{ background: 'linear-gradient(135deg, #FFD04F, #F58A25, #F2632F)' }}
          />
          <div className="relative bg-zinc-950 rounded-3xl p-8 md:p-12 border border-zinc-800">
            <div className="flex flex-col md:flex-row items-center gap-8">
              {/* Left: Mockup representation */}
              <div className="flex-1">
                <div className="relative">
                  {/* Simulated dashboard */}
                  <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6 shadow-2xl">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                      <span className="ml-2 text-zinc-600 text-xs">arenai.com.br/dashboard</span>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-zinc-400 text-sm">Receita Mensal</span>
                        <span className="text-green-400 font-bold">R$ 24.850</span>
                      </div>
                      <div className="w-full bg-zinc-800 rounded-full h-2">
                        <div
                          className="h-2 rounded-full"
                          style={{ width: '78%', background: 'linear-gradient(90deg, #FFD04F, #F58A25)' }}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-zinc-400 text-sm">Ocupação</span>
                        <span className="text-yellow-400 font-bold">78%</span>
                      </div>
                      <div className="w-full bg-zinc-800 rounded-full h-2">
                        <div
                          className="h-2 rounded-full bg-blue-500"
                          style={{ width: '92%' }}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-zinc-400 text-sm">Alunos Ativos</span>
                        <span className="text-blue-400 font-bold">142</span>
                      </div>
                    </div>
                  </div>
                  {/* Floating cards */}
                  <div className="absolute -top-4 -right-4 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 shadow-lg">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-green-400" />
                      <span className="text-xs text-green-400 font-medium">99.9% uptime</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Value prop */}
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  Gestão + Vídeos.
                  <br />
                  <span className="text-zinc-500">Tudo integrado.</span>
                </h3>
                <p className="text-zinc-400 mb-6 leading-relaxed">
                  O ArenAi cuida da gestão completa da sua quadra — agenda, cobranças, alunos,
                  torneios e analytics. A Apertai cuida dos vídeos e das lives. Juntos, são
                  a solução mais completa do mercado.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                  <a
                    href="https://arenai.com.br"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm text-black shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 no-underline"
                    style={{ background: 'linear-gradient(135deg, #FFD04F, #F58A25)' }}
                  >
                    Conhecer o ArenAi
                  </a>
                  <a
                    href="/contract-camera"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm text-zinc-300 border border-zinc-700 hover:border-yellow-500/50 hover:text-yellow-400 transition-all duration-300 no-underline"
                  >
                    Fechar os dois
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features grid */}
        <div className="grid grid-cols-2 gap-4 md:gap-6 lg:grid-cols-4">
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                className="group p-4 md:p-6 rounded-xl bg-zinc-900/50 border border-zinc-800 hover:border-yellow-500/30 transition-all duration-300"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Icon className="w-4 h-4 text-yellow-400 group-hover:text-yellow-300 transition-colors" />
                  <h3 className="text-sm font-semibold text-white">{feature.title}</h3>
                </div>
                <p className="text-zinc-500 text-xs md:text-sm leading-relaxed">{feature.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
